import { getAdmByNumber, getCustomerByNumber } from "@/graphql/schemas/shared/utils/getCustomer"
import { getEmployeesByNumber } from "@/graphql/schemas/shared/utils/getEmployees"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getSanityOfficesAndArticles } from "../utils/orderHelpers"

export const queryCustomerOrdersByNumber = {
  async customerOrdersByNumber(_parent, args, context, _info) {
    const { orderNumbers, cdn, lang } = args

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ORDERS,
      operation: Operations.READ,
    })

    let graphiQLQueryObject = []

    orderNumbers.forEach((orderNumber) => {
      const tmp = {
        method: `order_${orderNumber}: findOrder`,
        variables: { orderNumber },
        fields: [
          "orderNumber",
          "customerNumber",
          "companyName1",
          "companyName2",
          "street",
          "country",
          "postalCode",
          "city",
          "additionalStreetInfo",
          "infoText1",
          "infoText2",
          "infoText3",
          "discount",
          "paymentMethod",
          "discountKey",
          "deliveryMethod",
          "deliveryMethodDescription",
          "shippingAddress1",
          "shippingAddress2",
          "shippingStreet",
          "shippingCountry",
          "shippingPostalCode",
          "shippingCity",
          "shippingAdditionalStreetInfo",
          "vatKey",
          "orderType",
          "orderNumber2",
          "orderDate",
          "shippingCosts",
          "packagingCosts",
          "otherCosts",
          "shippingDate",
          "customerOrderNumber",
          "customerOrderText",
          "admNumber",
          "employeeNumber",
          "totalAmount",
          "branchNumber",
          "customerBranchNumber",
          "orderStatus",
          "valueOfGoods",
          "discountAmount",
          "discountPercent",
          "grossInvoiceAmount",
          "netInvoiceAmount",
          "vatPercent",
          "vatAmount",
          "groupBillingKey",
        ],
        expandedFields: {
          items: [
            "posId",
            "sortNumber",
            "sku",
            "quantity",
            "contents",
            "amount",
            "price",
            "description",
            "additionalDescription: description2",
            "discount",
            "totalPrice",
            "freightWeight",
            "totalFreightWeight",
            "itemStatus",
          ],
        },
      }
      graphiQLQueryObject.push(tmp)
    })

    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const customerOrderResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data

    if (!customerOrderResponse) throw new Error("Customer order by number not found")

    return await orderNumbers.map(async (orderNumber) => {
      if (!customerOrderResponse[`order_${orderNumber}`]) {
        return null
      }

      const orderResponse = customerOrderResponse[`order_${orderNumber}`]
      await validateAccessRightsToCustomer({
        user: user,
        customerNumber: orderResponse.customerNumber,
      })

      const articleSkusIds = orderResponse?.items.map((item) => item?.sku)
      const customerBranchNumber = orderResponse?.customerBranchNumber
      const branchNumber = orderResponse?.branchNumber

      const { customerBranch, branch, articles } = await getSanityOfficesAndArticles(
        { articleSkus: articleSkusIds, customerBranch: customerBranchNumber, branch: branchNumber },
        client,
      )

      const customerObject = await getCustomerByNumber(orderResponse?.customerNumber)
      const admObject = await getAdmByNumber(orderResponse?.admNumber)
      const employeeObject = await getEmployeesByNumber(orderResponse?.employeeNumber, client)
      let totalFreightWeight = 0

      const orderItems = orderResponse?.items?.map((item) => {
        if (!item) return null

        const article = articles?.find((article) => article?.sku === item.sku)
        totalFreightWeight = totalFreightWeight + Number(item?.totalFreightWeight)

        return {
          article,
          sortNumber: item?.sortNumber,
          totalFreightWeight: item?.totalFreightWeight,
          totalPrice: item?.totalPrice,
          quantity: item?.quantity,
          price: item?.price,
          itemStatus: item?.itemStatus,
          freightWeight: item?.freightWeight,
          amount: item?.amount,
          discount: item?.discount,
          description: item?.description,
          additionalDescription: item?.additionalDescription,
          positionId: item?.posId,
        }
      })

      return {
        ...orderResponse,
        items: orderItems,
        orderNumberLong: orderResponse?.orderNumber,
        orderNumberShort: orderResponse?.orderNumber2,
        customer: customerObject,
        admUser: admObject,
        employeeUser: employeeObject,
        totalFreightWeight,
        customerBranch,
        branch,
      }
    })
  },
}
